import { useDeviceInventory } from "../../hooks/queries";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Skeleton } from "../../components/ui/Skeleton";
import { ErrorState } from "../../components/ui/States";
import { Badge } from "../../components/ui/Badge";
import { Table, TBody, TD, TH, THead, TR } from "../../components/ui/Table";
import { formatBytes, formatDateTime, formatInteger, formatPercent } from "../../lib/format";

function InfoItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}

export function DeviceHardwareTab({ deviceId }: { deviceId: string }) {
  const query = useDeviceInventory(deviceId);

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        title="Donanım bilgisi alınamadı"
        message="Envanter verisi yüklenemedi. Backend bağlandığında bu sekme /devices/{id}/inventory ucundan beslenecek."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const report = query.data;
  const hardware = report.hardware;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Donanım bilgileri</CardTitle>
          <CardDescription>
            Envanter toplama zamanı: {formatDateTime(report.collectedAt)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <InfoItem label="Üretici" value={hardware.manufacturer ?? "—"} />
            <InfoItem label="Model" value={hardware.model ?? "—"} />
            <InfoItem label="Seri no" value={hardware.serialNumber ?? "—"} />
            <InfoItem
              label="Bellek"
              value={formatBytes(hardware.memoryBytes)}
            />
            <InfoItem label="CPU modeli" value={hardware.cpuModel ?? "—"} />
            <InfoItem
              label="Çekirdek"
              value={`${formatInteger(hardware.cpuCores)} fiziksel / ${formatInteger(hardware.cpuLogical)} mantıksal`}
            />
            <InfoItem label="Hostname" value={report.hostname} />
            <InfoItem
              label="Agent sürümü"
              value={<Badge tone="muted">{report.agentVersion}</Badge>}
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Diskler</CardTitle>
          <CardDescription>Bağlama noktalarına göre kapasite kullanımı</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Bağlama</TH>
                <TH>Aygıt</TH>
                <TH>Dosya sistemi</TH>
                <TH>Toplam</TH>
                <TH>Kullanılan</TH>
                <TH>Kullanım</TH>
              </TR>
            </THead>
            <TBody>
              {(hardware.disks ?? []).map((disk) => {
                const ratio =
                  disk.totalBytes && disk.totalBytes > 0 && disk.usedBytes !== undefined
                    ? (disk.usedBytes / disk.totalBytes) * 100
                    : undefined;
                return (
                  <TR key={`${disk.mount}-${disk.device ?? ""}`}>
                    <TD className="font-mono text-xs">{disk.mount}</TD>
                    <TD className="text-muted-foreground font-mono text-xs">
                      {disk.device ?? "—"}
                    </TD>
                    <TD className="text-muted-foreground text-xs">{disk.fsType ?? "—"}</TD>
                    <TD>{formatBytes(disk.totalBytes)}</TD>
                    <TD>{formatBytes(disk.usedBytes)}</TD>
                    <TD>
                      <div className="flex items-center gap-2">
                        <div
                          role="meter"
                          aria-valuenow={Math.round(ratio ?? 0)}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={`${disk.mount} kullanım oranı`}
                          className="bg-muted h-2 w-24 overflow-hidden rounded-full"
                        >
                          <div
                            className={
                              (ratio ?? 0) > 90
                                ? "bg-danger h-full rounded-full"
                                : (ratio ?? 0) > 75
                                  ? "bg-warning h-full rounded-full"
                                  : "bg-success h-full rounded-full"
                            }
                            style={{ width: `${Math.min(100, ratio ?? 0)}%` }}
                          />
                        </div>
                        <span className="text-muted-foreground text-xs">
                          {formatPercent(ratio)}
                        </span>
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ağ arayüzleri</CardTitle>
          <CardDescription>Arayüz durumu ve adresler</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Arayüz</TH>
                <TH>MAC</TH>
                <TH>IP adresleri</TH>
                <TH>Hız</TH>
                <TH>Durum</TH>
              </TR>
            </THead>
            <TBody>
              {(hardware.nics ?? []).map((nic) => (
                <TR key={nic.name}>
                  <TD className="font-mono text-xs">{nic.name}</TD>
                  <TD className="text-muted-foreground font-mono text-xs">{nic.mac ?? "—"}</TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {(nic.ips ?? []).map((ip) => (
                        <Badge key={ip} tone="muted">
                          {ip}
                        </Badge>
                      ))}
                      {(nic.ips ?? []).length === 0 ? "—" : null}
                    </div>
                  </TD>
                  <TD className="text-muted-foreground text-xs">
                    {nic.speedBps ? `${formatInteger(nic.speedBps / 1_000_000)} Mbps` : "—"}
                  </TD>
                  <TD>
                    {nic.up ? (
                      <Badge tone="success">Açık</Badge>
                    ) : (
                      <Badge tone="danger">Kapalı</Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}